/**
 * connections-probe — single-flight `/api/integrations/my-connections` probe.
 *
 * Why this exists: every AuthCard + ComposioAuthInput component instance
 * needs to know "is toolkit X currently connected?" on mount and on
 * visibility change. A long chat history can render 10+ AuthCards at once;
 * naive per-component fetches mean 10 simultaneous requests on tab focus.
 *
 * The dedup is intentionally simple — a single in-flight promise + a 2s
 * freshness window. That collapses bursts (mount + immediate
 * visibilitychange) into one network call without introducing cache
 * invalidation we'd later have to debug. After 2s the cache is dropped;
 * the next probe re-fetches.
 *
 * Used only for the on-mount / on-focus connection state derivation in
 * AuthCard and ComposioAuthInput. NOT a general-purpose connection store
 * — for that, use the integrations hook.
 */

interface ConnectionRow {
  toolkit_slug: string;
  status: string;
  /** Grouped wire (MULTI_ACCOUNT_V1): per-account rows; absent flag-off. */
  accounts?: Array<{ id: string; status: string }>;
}

const FRESH_WINDOW_MS = 2000;

let inFlight: Promise<ConnectionRow[] | null> | null = null;
let cached: { rows: ConnectionRow[] | null; fetchedAt: number } | null = null;

/** `null` = INCONCLUSIVE (see `probeRows`); `[]` = the user genuinely has none. */
async function fetchConnections(
  token: string
): Promise<ConnectionRow[] | null> {
  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL || "";
  const r = await fetch(`${serverUrl}/api/integrations/my-connections`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) return null;
  const json = (await r.json()) as { connections?: ConnectionRow[] };
  return json.connections ?? [];
}

/**
 * Read the connection rows through the single-flight + freshness cache.
 *
 * - If a probe is in flight, joins it (zero extra network).
 * - If a result is <2s old, returns it from cache (zero network).
 * - Otherwise, fetches once.
 *
 * Never throws. Resolves to `null` when the read was INCONCLUSIVE (non-ok
 * response or a thrown fetch) — which is NOT the same as `[]`, the user
 * conclusively having no connections. This mirrors the server's own
 * null-for-inconclusive rule in `listAccountRecords`
 * (apps/server/src/services/composio.service.ts): any caller that decides on
 * ABSENCE decides wrong — silently, and without healing — if it cannot tell an
 * unreadable answer from an empty one.
 *
 * The two probes below then differ ON PURPOSE, because they ask different
 * questions of the same bytes: `probeIsToolkitConnected` collapses null to `[]`
 * (an unreadable answer and an empty one both mean "not connected right now",
 * and the next good read heals it), while `makeNewAccountProbe` must refuse to
 * record a baseline it could not actually see.
 */
async function probeRows(token: string): Promise<ConnectionRow[] | null> {
  const now = Date.now();
  if (cached && now - cached.fetchedAt < FRESH_WINDOW_MS) return cached.rows;

  if (!inFlight) {
    inFlight = fetchConnections(token)
      .then((rows) => {
        cached = { rows, fetchedAt: Date.now() };
        return rows;
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }

  return inFlight;
}

/**
 * Probe whether `toolkit` is currently connected (status === 'active').
 *
 * An unreadable read answers `false`, exactly as it always has.
 */
export async function probeIsToolkitConnected(
  token: string,
  toolkit: string
): Promise<boolean> {
  const slug = toolkit.toLowerCase();
  const rows = (await probeRows(token)) ?? [];
  return rows.some(
    (c) => c.toolkit_slug?.toLowerCase() === slug && c.status === "active"
  );
}

/**
 * Probe factory for ADD-ACCOUNT asks. "Connected" must mean a NEW account
 * appeared — not "the toolkit has an ACTIVE sibling", which is true the moment
 * the ask renders and would flash Connected before the user did anything.
 * First call records the baseline account ids and reports false; later calls
 * report true once an unseen id shows up. On a wire without accounts[] it
 * never flips — the substrate resolution (verify → SYSTEM_RESUME → unmount)
 * remains the success path, only the pre-verify flash is lost.
 *
 * Only ACTIVE accounts count, on BOTH sides of the comparison. Composio mints
 * the `ca_*` row at authorize() time — the SDK reports INITIATED, which our
 * vocabulary maps to 'pending' — while the consent screen is still open, so a
 * status-blind id diff would flash Connected on the click, not on the grant.
 * Filtering the baseline too is what lets that same id flip the probe once it
 * turns active; baselining it raw would swallow the one id that was ever going
 * to be new.
 *
 * An INCONCLUSIVE read records NO baseline (and answers false). Baselining ∅
 * off a 401 would make the user's existing ACTIVE account read as "new" on the
 * next successful poll — a false Connected that never heals, since the baseline
 * is recorded once and that account never goes away. A conclusive empty read
 * still baselines, so connecting a first account through an add-mode ask flips
 * the probe normally.
 */
export function makeNewAccountProbe(): (
  token: string,
  toolkit: string
) => Promise<boolean> {
  let baseline: Set<string> | null = null;
  return async (token: string, toolkit: string): Promise<boolean> => {
    const slug = toolkit.toLowerCase();
    const rows = await probeRows(token);
    if (rows === null) return false;
    const ids = (
      rows.find((c) => c.toolkit_slug?.toLowerCase() === slug)?.accounts ?? []
    )
      .filter((a) => a.status === "active")
      .map((a) => a.id);
    if (baseline === null) {
      baseline = new Set(ids);
      return false;
    }
    return ids.some((id) => !baseline!.has(id));
  };
}

// Test-only helpers. Never call from production code.
export function _resetConnectionsProbeForTest(): void {
  inFlight = null;
  cached = null;
}
